import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
const features = [{
  name: 'Cost Per Use',
  ginext: '$1.47',
  menthol: '$2.10',
  generic: '$0.90',
  type: 'price' as const
}, {
  name: 'Supports Circulation',
  ginext: true,
  menthol: false,
  generic: false,
  type: 'bool' as const
}, {
  name: 'No Cold Shock',
  ginext: true,
  menthol: false,
  generic: true,
  type: 'bool' as const
}, {
  name: 'Absorbs Cleanly',
  ginext: true,
  menthol: false,
  generic: false,
  type: 'bool' as const
}, {
  name: 'Botanical Formula',
  ginext: true,
  menthol: false,
  generic: true,
  type: 'bool' as const
}, {
  name: 'Daily Use Friendly',
  ginext: true,
  menthol: false,
  generic: false,
  type: 'bool' as const
}, {
  name: 'Reduces Leg Swelling',
  ginext: true,
  menthol: false,
  generic: false,
  type: 'bool' as const
}, {
  name: 'Third-Party Tested',
  ginext: true,
  menthol: false,
  generic: false,
  type: 'bool' as const
}];
const pills = ['Deep warming relief, not numbing', '100% botanical — zero synthetics', 'Absorbs clean, no greasy film', 'Supports circulation daily', 'Third-party tested & reviewed'];
function BoolIcon({
  value
}: {
  value: boolean;
}) {
  if (value) {
    return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white" data-id="element-472">
        <Check className="w-3.5 h-3.5" strokeWidth={3} data-id="element-473" />
      </span>;
  }
  return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-400" data-id="element-474">
      <X className="w-3.5 h-3.5" strokeWidth={3} data-id="element-475" />
    </span>;
}
export function CompareSection() {
  return <section className="bg-[#F5F0EB] py-10 px-4 sm:py-16 sm:px-6" data-id="element-476">
      <div className="max-w-[1100px] mx-auto lg:grid lg:grid-cols-[280px_1fr] lg:gap-14 lg:items-start" data-id="element-477">
        {/* Left / Top: Intro */}
        <div className="max-w-[480px] mx-auto lg:max-w-none lg:mx-0 mb-7 lg:mb-0" data-id="element-478">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} data-id="element-479">
            <p className="text-[11px] font-extrabold tracking-[0.12em] uppercase text-ginger text-center lg:text-left mb-2" data-id="element-480">
              The Honest Comparison
            </p>
            <h2 className="text-[28px] lg:text-[36px] font-black text-[#2D2D2D] text-center lg:text-left leading-[1.1] mb-2.5" data-id="element-481">
              <span className="text-ginger" data-id="element-482">Us</span> vs. Them
            </h2>
            <p className="text-sm text-[#6B6B6B] text-center lg:text-left leading-relaxed mb-7" data-id="element-483">
              Not overhyped. Not overpriced. Real circulation support that works{' '}
              <em data-id="element-484">with</em> your body — not against it.
            </p>
          </motion.div>

          {/* Benefit Pills - Desktop: below intro, Mobile: below table */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="hidden lg:flex flex-col gap-2.5" data-id="element-485">
            {pills.map((pill, i) => <div key={i} className="flex items-center gap-3 bg-white border border-[#E0D8CF] rounded-[10px] px-4 py-3 text-[13px] font-semibold text-[#2D2D2D] shadow-sm" data-id="element-486">
                <span className="w-[22px] h-[22px] rounded-full bg-ginger text-white flex items-center justify-center flex-shrink-0" data-id="element-487">
                  <Check className="w-3 h-3" strokeWidth={3} data-id="element-488" />
                </span>
                {pill}
              </div>)}
          </motion.div>
        </div>

        {/* Right / Middle: Table */}
        <div data-id="element-489">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="bg-white rounded-2xl overflow-hidden shadow-md max-w-[480px] mx-auto lg:max-w-none lg:mx-0 mb-5 lg:mb-4" data-id="element-490">
            {/* Column Headers */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr]" data-id="element-491">
              <div className="px-4 py-3 text-left text-[10px] lg:text-xs font-medium text-[#6B6B6B] border-b-2 border-[#E0D8CF]" data-id="element-492">
                Feature
              </div>
              <div className="px-2 py-3 text-center text-[12px] lg:text-[13px] font-extrabold text-white bg-ginger border-b-2 border-ginger" data-id="element-493">
                GINEXT
              </div>
              <div className="px-2 py-3 text-center text-[11px] lg:text-xs font-extrabold text-[#2D2D2D] border-b-2 border-[#E0D8CF] leading-tight" data-id="element-494">
                Menthol
                <br data-id="element-495" />
                Cream
              </div>
              <div className="px-2 py-3 text-center text-[11px] lg:text-xs font-extrabold text-[#2D2D2D] border-b-2 border-[#E0D8CF] leading-tight" data-id="element-496">
                Generic
                <br data-id="element-497" />
                Oil
              </div>
            </div>

            {/* Data Rows */}
            {features.map((feature, i) => <div key={i} className={`grid grid-cols-[2fr_1fr_1fr_1fr] items-center min-h-[50px] border-b border-[#E0D8CF] last:border-b-0`} data-id="element-498">
                <div className={`px-4 py-3 text-[12px] lg:text-[13px] font-semibold text-[#2D2D2D] leading-tight ${i % 2 === 1 ? 'bg-[#FAFAF8]' : 'bg-white'}`} data-id="element-499">
                  {feature.name}
                </div>
                <div className={`px-2 py-3 text-center ${i % 2 === 1 ? 'bg-[#FFF8F0]' : 'bg-[#FFF4E8]'}`} data-id="element-500">
                  {feature.type === 'price' ? <span className="text-[13px] font-black text-ginger" data-id="element-501">
                      {feature.ginext}
                    </span> : <BoolIcon value={feature.ginext as boolean} data-id="element-502" />}
                </div>
                <div className={`px-2 py-3 text-center ${i % 2 === 1 ? 'bg-[#FAFAF8]' : 'bg-white'}`} data-id="element-503">
                  {feature.type === 'price' ? <span className="text-[12px] font-bold text-[#6B6B6B]" data-id="element-504">
                      {feature.menthol}
                    </span> : <BoolIcon value={feature.menthol as boolean} data-id="element-505" />}
                </div>
                <div className={`px-2 py-3 text-center ${i % 2 === 1 ? 'bg-[#FAFAF8]' : 'bg-white'}`} data-id="element-506">
                  {feature.type === 'price' ? <span className="text-[12px] font-bold text-[#6B6B6B]" data-id="element-507">
                      {feature.generic}
                    </span> : <BoolIcon value={feature.generic as boolean} data-id="element-508" />}
                </div>
              </div>)}
          </motion.div>

          {/* Benefit Pills - Mobile only */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="flex flex-col gap-2.5 max-w-[480px] mx-auto mb-5 lg:hidden" data-id="element-509">
            {pills.map((pill, i) => <div key={i} className="flex items-center gap-3 bg-white border border-[#E0D8CF] rounded-[10px] px-4 py-3 text-[13px] font-semibold text-[#2D2D2D] shadow-sm" data-id="element-510">
                <span className="w-[22px] h-[22px] rounded-full bg-ginger text-white flex items-center justify-center flex-shrink-0" data-id="element-511">
                  <Check className="w-3 h-3" strokeWidth={3} data-id="element-512" />
                </span>
                {pill}
              </div>)}
          </motion.div>

          {/* Footnote */}
          <p className="text-[11px] text-[#6B6B6B] italic text-center lg:text-left leading-relaxed max-w-[480px] mx-auto lg:max-w-none lg:mx-0" data-id="element-513">
            *Based on publicly available product formulations and independent
            lab testing.
          </p>
        </div>
      </div>
    </section>;
}